/**
 * Hook that resolves SEO meta data from API compositions into a structure
 * ready for the SeoHead component. Handles fallback chains and derivation logic.
 *
 * Fallback strategy: trust backend-resolved values within each composition.
 * Only fall back at the frontend level when an entire composition is absent.
 */

import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getEnv } from "@/config/env";
import {
  buildLocalePath,
  extractLocaleFromPath,
  stripLocaleFromPath,
} from "@/utils/locale-url";
import type {
  MetaDataComposition,
  OpenGraphComposition,
  TwitterCardComposition,
  ResolvedSeoMeta,
  HreflangLink,
} from "@/types/seo";

const SITE_NAME = "WeHunt";
const DEFAULT_LOCALE = "en";

/** Page type alias → og:type mapping */
const OG_TYPE_MAP: Record<string, string> = {
  device: "product",
  accessory: "product",
  license: "product",
  productPage: "product",
  articlePage: "article",
  articleListingPage: "article",
};

export interface UseSeoMetaParams {
  metaData?: MetaDataComposition;
  openGraph?: OpenGraphComposition;
  twitterCard?: TwitterCardComposition;
  pageType: string;
  alternateUrls?: Record<string, string>;
  publishedTime?: string | null;
  productPrice?: { amount: string; currency: string } | null;
}

export function useSeoMeta(params: UseSeoMetaParams): ResolvedSeoMeta {
  const { pathname } = useLocation();

  return useMemo(() => resolveSeoMeta(params, pathname), [params, pathname]);
}

/**
 * Pure function for resolving SEO meta — separated from the hook for testability.
 */
export function resolveSeoMeta(
  params: UseSeoMetaParams,
  pathname: string
): ResolvedSeoMeta {
  const {
    metaData,
    openGraph,
    twitterCard,
    pageType,
    alternateUrls,
    publishedTime,
    productPrice,
  } = params;

  const env = getEnv();
  const publicSiteUrl = env.publicSiteUrl.replace(/\/$/, "");

  // Title
  const rawTitle = metaData?.metaTitle ?? "";
  const title = rawTitle ? `${rawTitle} | ${SITE_NAME}` : SITE_NAME;

  // Description
  const description = metaData?.metaDescription ?? "";

  // Robots
  const robots = metaData?.metaNoIndex ? "noindex, nofollow" : "index, follow";

  // og:type derived from page content type
  const ogType = OG_TYPE_MAP[pageType] ?? "website";

  // og:url
  const ogUrl = publicSiteUrl ? `${publicSiteUrl}${pathname}` : pathname;

  // og:locale
  const ogLocale = metaData?.metaLocale || DEFAULT_LOCALE;

  // Images — resolve absolute URLs
  const metaImageUrl = resolveImageUrl(
    metaData?.metaImage?.url,
    env.apiBaseUrl
  );
  const ogImageUrl =
    resolveImageUrl(openGraph?.ogImage?.url, env.apiBaseUrl) ?? metaImageUrl;
  const twitterImageUrl =
    resolveImageUrl(twitterCard?.twitterImage?.url, env.apiBaseUrl) ??
    ogImageUrl;

  // OG — use composition values, fall back to meta when composition is absent
  const ogTitle = openGraph?.ogTitle ?? rawTitle ?? "";
  const ogDescription = openGraph?.ogDescription ?? description;
  const ogImage = openGraph?.ogImage ?? metaData?.metaImage;

  // Twitter — use composition values, fall back to OG when composition is absent
  const twitterTitle = twitterCard?.twitterTitle ?? ogTitle;
  const twitterDescription = twitterCard?.twitterDescription ?? ogDescription;
  const twitterCardType = twitterCard?.twitterCardType ?? "summary_large_image";

  // Canonical — self-referencing: the link for the culture this page is served
  // in, never another language version (those are hreflang alternates). The
  // backend reports cultures in lower case ("nb-no") while the URL locale may
  // be either case, hence the case-insensitive match.
  const pageCulture = extractLocaleFromPath(pathname) ?? DEFAULT_LOCALE;
  const findLink = (culture: string) =>
    metaData?.metaLinks?.find(
      (link) => link.culture?.toLowerCase() === culture.toLowerCase()
    );
  const canonicalLink = findLink(pageCulture);
  const canonicalUrl = canonicalLink
    ? absoluteUrl(publicSiteUrl, canonicalLink.url)
    : null;

  // x-default points at the global English version
  const defaultLink = findLink(DEFAULT_LOCALE);
  const xDefaultUrl = defaultLink
    ? absoluteUrl(publicSiteUrl, defaultLink.url)
    : null;

  // Hreflang alternates
  const alternates = buildAlternates(
    metaData?.metaLinks,
    alternateUrls,
    publicSiteUrl,
    xDefaultUrl
  );

  // Article meta
  const article = publishedTime ? { publishedTime } : null;

  // Product meta
  const product = productPrice
    ? { priceAmount: productPrice.amount, priceCurrency: productPrice.currency }
    : null;

  return {
    title,
    description,
    robots,
    canonicalUrl,
    alternates,
    og: {
      title: ogTitle,
      description: ogDescription,
      image: ogImageUrl,
      imageWidth: ogImage?.width ?? null,
      imageHeight: ogImage?.height ?? null,
      imageAlt: ogImage?.altText ?? null,
      url: ogUrl,
      type: ogType,
      locale: ogLocale,
      siteName: SITE_NAME,
    },
    twitter: {
      cardType: twitterCardType,
      title: twitterTitle,
      description: twitterDescription,
      image: twitterImageUrl,
    },
    article,
    product,
  };
}

/** Resolve image URL to absolute — prefix with API base if relative */
function resolveImageUrl(
  url: string | undefined | null,
  apiBaseUrl: string
): string | null {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  // Strip /api/v2 suffix from API base to get the media host
  const mediaHost = apiBaseUrl.replace(/\/api\/v\d+$/, "");
  return `${mediaHost}${url}`;
}

/**
 * Absolute URL for a backend path, with the locale segment in its lower-case
 * URL form — paths are case-sensitive for search engines (GROW-391).
 */
function absoluteUrl(publicSiteUrl: string, path: string): string {
  const locale = extractLocaleFromPath(path);
  const urlPath = locale
    ? buildLocalePath(locale, stripLocaleFromPath(path))
    : path;
  return `${publicSiteUrl}${urlPath}`;
}

/** Build hreflang alternate links from metaLinks + alternateUrls */
function buildAlternates(
  metaLinks: MetaDataComposition["metaLinks"] | undefined,
  alternateUrls: Record<string, string> | undefined,
  publicSiteUrl: string,
  xDefaultUrl: string | null
): HreflangLink[] {
  const alternates: HreflangLink[] = [];

  // Add x-default pointing to the global English version
  if (xDefaultUrl) {
    alternates.push({ hreflang: "x-default", href: xDefaultUrl });
  }

  // Use metaLinks for culture-specific alternates (backend provides full URL
  // paths). Every language version is listed, including English and the page
  // itself, matching the sitemap's xhtml:link set.
  if (metaLinks) {
    for (const link of metaLinks) {
      if (link.culture) {
        alternates.push({
          hreflang: link.culture,
          href: absoluteUrl(publicSiteUrl, link.url),
        });
      }
    }
  }

  // Fallback: if no metaLinks, use alternateUrls from the content response
  if (!metaLinks?.length && alternateUrls) {
    for (const [culture, url] of Object.entries(alternateUrls)) {
      alternates.push({
        hreflang: culture,
        href: absoluteUrl(publicSiteUrl, url),
      });
    }
  }

  return alternates;
}
